import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { MEMBER_ID_REGEX } from '@chillout/core'
import { supabase } from '../../../lib/supabase'

export interface AdminMemberSearchResult {
  id: string
  firstName: string | null
  lastName: string | null
  email: string
  currentStamps: number
  stampTarget: number
  activeRewardCount: number
}

interface RawSearchRow {
  id: string
  first_name: string | null
  last_name: string | null
  email: string | null
  stamp_cards: { current_stamps: number } | { current_stamps: number }[] | null
}

const PAGE_SIZE = 20

async function searchMembers(query: string, page: number): Promise<{ results: Omit<AdminMemberSearchResult, 'stampTarget' | 'activeRewardCount'>[]; totalCount: number }> {
  const normalized = query.trim()
  const from = (page - 1) * PAGE_SIZE
  const to = from + PAGE_SIZE - 1

  let dbQuery = supabase
    .from('member_profiles')
    .select('id, first_name, last_name, email, stamp_cards ( current_stamps )', { count: 'exact' })
    .eq('is_anonymised', false)
    .order('created_at', { ascending: false })
    .range(from, to)

  if (MEMBER_ID_REGEX.test(normalized)) {
    dbQuery = dbQuery.ilike('member_id', normalized)
  } else if (normalized.includes('@')) {
    dbQuery = dbQuery.ilike('email', `%${normalized}%`)
  } else {
    dbQuery = dbQuery.or(`first_name.ilike.%${normalized}%,last_name.ilike.%${normalized}%,email.ilike.%${normalized}%`)
  }

  const { data, count, error } = await dbQuery

  if (error) throw error

  const rows = (data ?? []) as unknown as RawSearchRow[]

  const results = rows.map((row) => {
    const stampCard = Array.isArray(row.stamp_cards) ? row.stamp_cards[0] : row.stamp_cards
    return {
      id: row.id,
      firstName: row.first_name,
      lastName: row.last_name,
      email: row.email ?? '',
      currentStamps: stampCard?.current_stamps ?? 0,
    }
  })

  return { results, totalCount: count ?? 0 }
}

async function fetchActiveStampTarget(): Promise<number> {
  const { data, error } = await supabase
    .from('loyalty_config')
    .select('stamp_target')
    .eq('status', 'active')
    .single()

  if (error) return 12
  return data.stamp_target
}

async function fetchRewardCounts(memberIds: string[]): Promise<Record<string, number>> {
  if (memberIds.length === 0) return {}

  const { data, error } = await supabase
    .from('rewards')
    .select('member_id')
    .in('member_id', memberIds)
    .eq('status', 'active')

  if (error) return {}

  const counts: Record<string, number> = {}
  for (const row of data ?? []) {
    counts[row.member_id] = (counts[row.member_id] ?? 0) + 1
  }
  return counts
}

interface UseAdminMemberSearchParams {
  query: string
  page: number
}

export function useAdminMemberSearch({ query, page }: UseAdminMemberSearchParams) {
  return useQuery({
    queryKey: ['admin-member-search', query, page],
    queryFn: async () => {
      const [stampTarget, { results: partialResults, totalCount }] = await Promise.all([
        fetchActiveStampTarget(),
        searchMembers(query, page),
      ])

      const rewardCounts = await fetchRewardCounts(partialResults.map((r) => r.id))

      const results: AdminMemberSearchResult[] = partialResults.map((r) => ({
        ...r,
        stampTarget,
        activeRewardCount: rewardCounts[r.id] ?? 0,
      }))

      return { results, totalCount }
    },
    enabled: query.trim().length >= 2,
    placeholderData: keepPreviousData,
  })
}

export { PAGE_SIZE as ADMIN_MEMBER_PAGE_SIZE }